import { dom, library } from '@fortawesome/fontawesome-svg-core';
import {
  faBookOpen,
  faLink,
  faChevronRight,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faBell,
  faPenToSquare,
  faCircleQuestion,
  faFileLines,
  faFileSignature,
} from '@fortawesome/pro-regular-svg-icons';
import { html, css, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { svgWithJs, variables } from '../variables';

@customElement('apt-section-round-border')
export class SectionRoundBorder extends LitElement {
  @property({ type: String })
  icon = '';
  @property({ type: String })
  sectionTitle = '';
  @property({ type: String })
  description = '';
  @property({ type: Boolean })
  showCaret = false;
  @property({ type: Boolean })
  showHr = false;
  @property({ type: URL })
  // Value must be set to undefined for the Nothing sentinel to remove the attribute.
  url = undefined;
  @property({ type: String })
  linkTarget = '_self';

  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        :host {
          font-family: var(--font-family-sans-serif);
          font-style: normal;
          font-size: 1rem;
        }

        a {
          cursor: pointer;
        }

        .no-anchor {
          color: inherit;
          text-decoration: none;
        }

        .round-border {
          padding: 10px;
          background-color: var(--white);
          border: 1px solid var(--border);
          border-radius: 10px;
        }

        .section-title {
          width: 100%;
        }

        .flex-container {
          display: flex;
          flex-direction: row;
        }

        .text-muted {
          font-size: 0.75rem;
          color: var(--gray-600);
        }

        .icon {
          font-size: 1rem;
          padding-top: 5px;
          padding-right: 1.5rem;
        }

        .icon-after {
          color: var(--gray-900);
          margin-left: auto;
          font-size: 0.75rem;
          padding-top: 5px;
          padding-left: 10px;
        }

        .green-bar {
          width: 100%;
          height: 1px;
          border: 0;
          background-color: var(--green);
        }

        .green {
          color: var(--green);
        }

        .display-hide {
          display: none;
        }
      `,
    ];
  }

  override render() {
    return this.showCaret
      ? html`<a
          href="${this.url}"
          target="${this.linkTarget ?? nothing}"
          class="no-anchor"
          data-test-selector="link"
        >
          <div data-test-selector="content" class="round-border">
            <div class="flex-container">
              ${this.getIcon()} ${this.getTitle()}
              <i class="fa fa-solid fa-chevron-right icon-after"></i>
            </div>
            ${this.getHorizontalLine()}
            <slot></slot>
          </div>
        </a> `
      : html`<div data-test-selector="content" class="round-border">
          <div class="flex-container">${this.getIcon()} ${this.getTitle()}</div>
          ${this.getHorizontalLine()}
          <slot></slot>
        </div>`;
  }

  protected override firstUpdated(): void {
    library.add(
      faBookOpen,
      faPenToSquare,
      faLink,
      faBell,
      faCircleQuestion,
      faChevronRight,
      faUsers,
      faFileLines,
      faFileSignature,
    );

    // @ts-ignore The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }

  getIcon() {
    return html`<span class="fa-layers icon">
      <i class="fa ${this.icon}"></i>
    </span>`;
  }
  getTitle() {
    return html`<div class="section-title">
      <span> ${this.sectionTitle} </span>
      <div class="text-muted">${this.description}</div>
    </div>`;
  }
  getHorizontalLine() {
    return html`<hr
      class=${classMap({
        'green-bar': this.showHr,
        'display-hide': !this.showHr,
      })}
    />`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-section-round-border': SectionRoundBorder;
  }
}
